// @soft-depth
import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { Brain, MessageSquare } from 'lucide-react';
import { discLabel } from '../../../constants/disc';
import DobReadings from './DobReadings';
import { IdeaCard } from './IdeaKit';

/**
 * Smart IDEA's 性格類型 card — DISC trait toggles, the 互動方式 callout and the
 * birthday readings (DobReadings). Takes the letter and a setter rather than
 * reading or writing the store: the host saves the personality its own way.
 * (招募 IDEA sets the candidate's type from its persona card instead.)
 *
 * Soft Depth (IdeaCard's scope): each trait pair is a pill with a sliding
 * accent thumb under the chosen half.
 */

type DiscLetter = 'D' | 'I' | 'S' | 'C';
type Trait = 'outgoing' | 'introverted' | 'logical' | 'emotional';

type IntlLike = {
  formatMessage: (descriptor: { id: string }, values?: Record<string, unknown>) => string;
  locale?: string;
};

export interface PersonalityCardProps {
  intl: IntlLike;
  /** The DISC letter on file; anything else reads as "not set". */
  personality?: string | null;
  onChangePersonality: (letter: DiscLetter) => void;
  dob?: string | Date | null;
  /** Where 「先設定生日」 goes. Without it the hint is plain text. */
  onSetBirthday?: () => void;
  /** Replaces the 「先設定生日」 hint entirely, e.g. with an inline date picker. */
  renderBirthdayHint?: () => React.ReactNode;
  /**
   * The card's heading. Each host names whose type it is — 客戶的性格類型 /
   * 候選人的性格類型 — so it cannot be mistaken for 我的溝通風格 beside it.
   */
  title?: string;
  /** Outlines the card as the one the dialog was opened for. */
  focused?: boolean;
  cardRef?: React.Ref<HTMLElement>;
}

// DISC letter → trait flags.
export const computeDiscTraits = (personality?: string | null) => ({
  outgoing: personality === 'D' || personality === 'I',
  introverted: personality === 'S' || personality === 'C',
  logical: personality === 'D' || personality === 'C',
  emotional: personality === 'I' || personality === 'S'
});

const isDiscLetter = (value?: string | null): value is DiscLetter =>
  value === 'D' || value === 'I' || value === 'S' || value === 'C';

/** The letter a trait tap produces, defaulting the untouched axis. */
const nextPersonalityFor = (current: string | null | undefined, trait: Trait): DiscLetter => {
  const next = computeDiscTraits(current);
  if (trait === 'outgoing' || trait === 'introverted') {
    next.outgoing = trait === 'outgoing';
    next.introverted = trait === 'introverted';
  } else {
    next.logical = trait === 'logical';
    next.emotional = trait === 'emotional';
  }
  if (!next.outgoing && !next.introverted) next.outgoing = true;
  if (!next.logical && !next.emotional) next.logical = true;

  if (next.outgoing) return next.logical ? 'D' : 'I';
  return next.emotional ? 'S' : 'C';
};

const PersonalityCard = (props: PersonalityCardProps) => {
  const { intl, personality, onChangePersonality, dob, onSetBirthday, renderBirthdayHint, focused, cardRef, title } =
    props;
  const t = (id: string) => intl.formatMessage({ id });

  const letter = isDiscLetter(personality) ? personality : undefined;
  const traits = computeDiscTraits(letter);
  const group1Value = traits.outgoing ? 'outgoing' : traits.introverted ? 'introverted' : '';
  const group1Index = traits.outgoing ? 0 : traits.introverted ? 1 : -1;
  const group2Value = traits.logical ? 'logical' : traits.emotional ? 'emotional' : '';
  const group2Index = traits.logical ? 0 : traits.emotional ? 1 : -1;

  const toggle = (trait: Trait | null) => {
    if (trait) onChangePersonality(nextPersonalityFor(letter, trait));
  };

  // A pair of traits as one pill; the thumb slides under the chosen half.
  const traitPair = (value: string, index: number, pair: [Trait, Trait]) => (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(e, val) => toggle(val)}
      sx={{
        position: 'relative',
        p: '2px',
        borderRadius: '999px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.100',
        overflow: 'hidden'
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          top: 2,
          bottom: 2,
          left: 2,
          width: 'calc(50% - 2px)',
          borderRadius: '999px',
          bgcolor: 'primary.main',
          transform: `translateX(${Math.max(index, 0) * 100}%)`,
          opacity: index === -1 ? 0 : 1,
          transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
        }}
      />
      {pair.map(trait => (
        <ToggleButton
          key={trait}
          value={trait}
          disableRipple
          sx={{
            zIndex: 1,
            minWidth: 80,
            height: 28,
            px: 1.75,
            border: 'none',
            borderRadius: '999px !important',
            typography: 'subtitle2',
            textTransform: 'none',
            color: 'text.secondary',
            transition: 'color 250ms ease',
            '&:hover, &.Mui-selected, &.Mui-selected:hover': { bgcolor: 'transparent' },
            '&.Mui-selected': { color: 'primary.contrastText' }
          }}
        >
          {t(trait)}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  return (
    <IdeaCard
      id="personality"
      testId="personality-card"
      cardRef={cardRef}
      focused={focused}
      data-letter={letter || undefined}
      icon={<Brain size={18} aria-hidden="true" />}
      title={title || t('Personality_short')}
    >
      <Stack sx={{ gap: 1.25, mb: 1.5 }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 26,
              height: 26,
              borderRadius: 0.75,
              bgcolor: 'primary.soft',
              color: 'primary.dark'
            }}
          >
            <Brain size={14} aria-hidden="true" />
          </Box>
          <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: 'grey.800' }}>
            {t('choose-personality')}
          </Typography>
        </Stack>
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
          {traitPair(group1Value, group1Index, ['outgoing', 'introverted'])}
          {traitPair(group2Value, group2Index, ['logical', 'emotional'])}
        </Stack>
      </Stack>

      {/* The type the two toggles land on, and how to talk to them. A plain
          tile like 星座 below, not a tinted box: it is a reading, not a warning. */}
      {letter && (
        <Box
          sx={{
            mb: 1.5,
            px: 1.5,
            py: 1.25,
            borderRadius: 1,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200'
          }}
        >
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
            <MessageSquare size={14} aria-hidden="true" />
            <Typography component="span" variant="subtitle2" sx={{ fontWeight: 700 }}>
              {t('Approach')}:
            </Typography>
          </Stack>
          <Typography component="div" variant="body1" sx={{ mt: 0.5, fontWeight: 700, color: 'grey.800' }}>
            {discLabel(letter, intl.locale)}
          </Typography>
          <Typography component="div" variant="body2" sx={{ mt: 0.25, fontWeight: 500, color: 'text.primary' }}>
            {t(`${letter}_approach`)}
          </Typography>
        </Box>
      )}

      <DobReadings intl={intl} dob={dob} onSetBirthday={onSetBirthday} renderBirthdayHint={renderBirthdayHint} />
    </IdeaCard>
  );
};

export default PersonalityCard;
